import React from 'react'
import { Theme, createStyles, makeStyles } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'
import styled from 'styled-components'

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    root: {
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 9999,
      width: '100%',
      height: '100%',
      background: 'rgba(256, 256, 256, .5)',
      transition: '.3s linear'
    },
    loading: {
      opacity: 1,
      visibility: 'visible'
    },
    loaded: {
      opacity: 0,
      visibility: 'hidden'
    }
  })
)

const Centering = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

interface LoadingState {
  isLoading: boolean
}

interface LoadingHandle {
  handleIsLoading: (isLoading: boolean) => void
}

const initLoadingProps: LoadingState & LoadingHandle = {
  isLoading: true,
  handleIsLoading: () => {}
}

const LoadingContext = React.createContext<LoadingState & LoadingHandle>(initLoadingProps)

export const LoadingOverlay: React.FC<{ isLoading: boolean }> = ({ isLoading }) => {
  const classes = useStyles()

  return (
    <div className={`${classes.root} ${isLoading ? classes.loading : classes.loaded}`}>
      <Box style={{ height: '100%', textAlign: 'center' }}>
        <Centering>
          <CircularProgress size={100} thickness={1} color="primary" />
        </Centering>
      </Box>
    </div>
  )
}

export const LoadingProvider: React.FC = ({ children }) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(initLoadingProps.isLoading)
  const handleIsLoading: LoadingHandle['handleIsLoading'] = React.useCallback(_isLoading => {
    setIsLoading(_isLoading)
  }, [])

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        handleIsLoading
      }}
    >
      <LoadingOverlay isLoading={isLoading} />
      {children}
    </LoadingContext.Provider>
  )
}

export const useLoading = () => React.useContext(LoadingContext)
